import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { client } from '../generated/client.gen';

export type JobListKind = 'processing' | 'enqueued' | 'scheduled' | 'succeeded' | 'failed';

export interface JobStats {
  enqueued: number;
  scheduled: number;
  processing: number;
  succeeded: number;
  failed: number;
  deleted: number;
  servers: number;
  recurring: number;
  queues: number;
}

export interface RecurringJob {
  id: string;
  cron?: string | null;
  queue?: string | null;
  job?: string | null;
  lastExecution?: string | null;
  nextExecution?: string | null;
  lastJobId?: string | null;
  lastJobState?: string | null;
  timeZoneId?: string | null;
  removed: boolean;
  error?: string | null;
}

export interface JobSummary {
  id: string;
  job: string;
  queue?: string | null;
  state: string;
  timestamp?: string | null;
  reason?: string | null;
  exceptionType?: string | null;
  exceptionMessage?: string | null;
}

export interface QueueInfo {
  name: string;
  length: number;
  fetched: number;
}

interface JobListResponse {
  data: JobSummary[];
  total: number;
  queue?: string | null;
}

// Mirror of JobsEndpoints.HangfireStateDto / HangfireJobDetailDto.
export interface HangfireState {
  stateName: string;
  reason?: string | null;
  createdAt: string;
  /** Storage-dependent bag; failed states carry ExceptionType/Message/Details. */
  data: Record<string, string>;
}

export interface HangfireJobDetail {
  id: string;
  job?: string | null;
  createdAt?: string | null;
  properties: Record<string, string>;
  history: HangfireState[];
}

const jobsKey = ['admin', 'jobs'] as const;
const jobStatsKey = ['admin', 'jobs', 'stats'] as const;
const recurringKey = ['admin', 'jobs', 'recurring'] as const;
const queuesKey = ['admin', 'jobs', 'queues'] as const;
const jobListKey = (kind: JobListKind, from: number, count: number, queue?: string) =>
  ['admin', 'jobs', 'list', kind, from, count, queue ?? null] as const;
const jobDetailKey = (id: string) => ['admin', 'jobs', 'detail', id] as const;

// Live kinds reflect in-flight work, so poll; terminal lists (succeeded/failed) don't.
const LIVE_KINDS: JobListKind[] = ['processing', 'enqueued', 'scheduled'];

function useJobStats() {
  return useQuery({
    queryKey: jobStatsKey,
    queryFn: async (): Promise<JobStats> => {
      const { data } = await client.instance.get<JobStats>('/admin/jobs/stats');
      return data;
    },
    refetchInterval: 4000,
  });
}

function useRecurringJobs() {
  return useQuery({
    queryKey: recurringKey,
    queryFn: async (): Promise<RecurringJob[]> => {
      const { data } = await client.instance.get<{ data: RecurringJob[] }>('/admin/jobs/recurring');
      return data.data;
    },
    refetchInterval: 10000,
  });
}

function useQueues() {
  return useQuery({
    queryKey: queuesKey,
    queryFn: async (): Promise<QueueInfo[]> => {
      const { data } = await client.instance.get<{ data: QueueInfo[] }>('/admin/jobs/queues');
      return data.data;
    },
  });
}

function useJobList(
  kind: JobListKind,
  { from, count, queue }: { from: number; count: number; queue?: string },
) {
  return useQuery({
    queryKey: jobListKey(kind, from, count, queue),
    queryFn: async (): Promise<JobListResponse> => {
      const params: Record<string, unknown> = { from, count };
      if (kind === 'enqueued' && queue) params.queue = queue;
      const { data } = await client.instance.get<JobListResponse>(`/admin/jobs/${kind}`, { params });
      return data;
    },
    refetchInterval: LIVE_KINDS.includes(kind) ? 4000 : false,
  });
}

function useJobDetail(id: string | null) {
  return useQuery({
    queryKey: jobDetailKey(id ?? ''),
    enabled: !!id,
    queryFn: async (): Promise<HangfireJobDetail> => {
      const { data } = await client.instance.get<HangfireJobDetail>(`/admin/jobs/${id}/detail`);
      return data;
    },
  });
}

function useRequeueJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await client.instance.post(`/admin/jobs/${id}/requeue`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: jobsKey });
    },
  });
}

function useDeleteJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await client.instance.delete(`/admin/jobs/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: jobsKey });
    },
  });
}

function useTriggerRecurring() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await client.instance.post(`/admin/jobs/recurring/${id}/trigger`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: recurringKey });
      qc.invalidateQueries({ queryKey: jobStatsKey });
    },
  });
}

export const jobsApi = {
  useJobStats,
  useRecurringJobs,
  useQueues,
  useJobList,
  useJobDetail,
  useRequeueJob,
  useDeleteJob,
  useTriggerRecurring,
};
